import type { Snippet, SnippetCategory } from '@itin/shared'
import { useMemo, useState } from 'react'
import { useT } from '../lib/i18n.js'
// One folding rule, not two. If the library of places and the library of texts
// disagreed about accents, "Sao" would find "São Paulo" in one list and nothing
// in the other, which reads as the search being broken rather than different.
import { fold } from './SavedPlacePicker.js'
import { Button, Input, Select } from './ui.js'

/**
 * Finding one paragraph in a library that has grown.
 *
 * The same three narrowing controls the places library uses, and for the same
 * reason: an advisor two years in has eighty of these, and a scrollable list of
 * eighty is a list she stops opening. Free text runs over the title, the
 * destination and the prose itself — she remembers the phrase, not the label she
 * filed it under.
 */

export const SNIPPET_CATEGORIES: readonly SnippetCategory[] = [
  'arrival',
  'transport',
  'practical',
  'dining',
  'safety',
  'packing',
  'other',
]

/**
 * Filters and orders the library for a given trip.
 *
 * Exported because two surfaces use it and they must agree: a text she can find
 * in the library card has to be findable from inside a day by the same words.
 */
export function narrowSnippets(
  all: Snippet[],
  { query, destination, category, here }: {
    query: string
    destination: string
    category: string
    /** The trip's city. Matches sort first; they are never filtered out. */
    here: string
  },
): Snippet[] {
  const needle = fold(query)
  const matches = all.filter((entry) => {
    // A text with no destination applies everywhere, so a destination filter
    // narrows to a city WITHOUT hiding the ones that are true of every trip.
    // Dropping "what to do if you lose your passport" because it is not tagged
    // Paris would hide it on exactly the screen where it is wanted.
    if (destination && entry.destination !== null && fold(entry.destination) !== fold(destination)) {
      return false
    }
    if (category && entry.category !== category) return false
    if (!needle) return true
    return (
      fold(entry.title).includes(needle) ||
      fold(entry.destination ?? '').includes(needle) ||
      fold(entry.body).includes(needle)
    )
  })

  // Here first, then the ones that are true anywhere, then elsewhere — and
  // never a filter. She may well want the Lisbon paragraph on a Paris trip.
  const city = fold(here)
  const rank = (entry: Snippet): number => {
    if (entry.destination === null) return 1
    const where = fold(entry.destination)
    if (city !== '' && (where.includes(city) || city.includes(where))) return 0
    return 2
  }
  // `sort` is stable, so entries of equal rank keep the order the server sent.
  return [...matches].sort((a, b) => rank(a) - rank(b))
}

/** Whether this text is filed under the city the trip is going to. */
export function appliesHere(entry: Snippet, here: string): boolean {
  if (entry.destination === null) return false
  const city = fold(here)
  const where = fold(entry.destination)
  return city !== '' && (where.includes(city) || city.includes(where))
}

/** The distinct destinations in her library, for the filter. Nulls are not one. */
export function snippetDestinations(all: Snippet[]): string[] {
  const named = all.map((s) => s.destination).filter((d): d is string => d !== null)
  return [...new Set(named)].sort((a, b) => a.localeCompare(b))
}

export function SnippetFilters({
  query,
  destination,
  category,
  destinations,
  onQuery,
  onDestination,
  onCategory,
}: {
  query: string
  destination: string
  category: string
  destinations: string[]
  onQuery(value: string): void
  onDestination(value: string): void
  onCategory(value: string): void
}) {
  const t = useT()
  return (
    <div className="flex flex-col gap-2">
      <Input
        type="search"
        value={query}
        aria-label={t('snippets.search')}
        placeholder={t('snippets.search')}
        onChange={(event) => onQuery(event.target.value)}
      />
      <div className="grid grid-cols-2 gap-2">
        <Select
          aria-label={t('snippets.filter.destination')}
          value={destination}
          onChange={(event) => onDestination(event.target.value)}
        >
          <option value="">{t('snippets.filter.allDestinations')}</option>
          {/* Only the destinations she has actually used. */}
          {destinations.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Select>
        <Select
          aria-label={t('snippets.filter.category')}
          value={category}
          onChange={(event) => onCategory(event.target.value)}
        >
          <option value="">{t('snippets.filter.allCategories')}</option>
          {SNIPPET_CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {t(`snippets.category.${value}`)}
            </option>
          ))}
        </Select>
      </div>
    </div>
  )
}

/**
 * The compact picker, opened from wherever she is typing.
 *
 * Two lines of the prose under the title, not just the title. A library of
 * eighty entries called things like "Chegada — Paris" is a library where she has
 * to insert one to find out whether it was the right one, and the undo for that
 * is deleting a paragraph out of the middle of a note by hand.
 */
export function SnippetPicker({
  snippets,
  loading,
  here,
  busy,
  onPick,
  onCancel,
}: {
  snippets: Snippet[]
  /**
   * The list is fetched when the picker opens, so its first frame has nothing
   * in it. Distinguished from a genuinely empty library because otherwise every
   * first open flashes "nothing kept yet" at somebody who has eighty.
   */
  loading: boolean
  here: string
  busy: boolean
  onPick(snippet: Snippet): void
  onCancel(): void
}) {
  const t = useT()
  const [query, setQuery] = useState('')
  const [destination, setDestination] = useState('')
  const [category, setCategory] = useState('')

  const destinations = useMemo(() => snippetDestinations(snippets), [snippets])
  const shown = useMemo(
    () => narrowSnippets(snippets, { query, destination, category, here }),
    [snippets, query, destination, category, here],
  )

  return (
    <div data-testid="snippet-picker" className="flex flex-col gap-3 rounded-md bg-slate-50 p-3">
      {loading ? (
        <p className="text-sm text-slate-500">{t('common.loading')}</p>
      ) : snippets.length === 0 ? (
        <p className="text-sm text-slate-500">{t('snippets.empty')}</p>
      ) : (
        <>
          {/* Said where the insert happens, not only on the library card. The
              copy rule is the thing she has to be able to trust. */}
          <p className="text-xs text-slate-500">{t('snippets.insertHint')}</p>

          <SnippetFilters
            query={query}
            destination={destination}
            category={category}
            destinations={destinations}
            onQuery={setQuery}
            onDestination={setDestination}
            onCategory={setCategory}
          />

          {shown.length === 0 ? (
            <p className="text-sm text-slate-500">{t('snippets.noMatches')}</p>
          ) : (
            <ul className="max-h-64 overflow-y-auto rounded-md border border-slate-200">
              {shown.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onPick(entry)}
                    className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-white disabled:opacity-50"
                  >
                    <span className="font-medium">{entry.title}</span>
                    <span className="text-xs text-slate-500">
                      {t(`snippets.category.${entry.category}`)} ·{' '}
                      {entry.destination ?? t('snippets.anywhere')}
                    </span>
                    {/* Plain text, deliberately not rendered as Markdown: this
                        is a preview of what she is about to paste, and showing
                        it formatted would hide the asterisks she is copying. */}
                    <span className="line-clamp-2 text-xs text-slate-600">{entry.body}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      <div>
        <Button type="button" variant="quiet" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}
